import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Link as MuiLink,
  TextField,
  Typography,
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import IconAddress from '../../assets/ic-address.png';
import IconEmail from '../../assets/ic-email.png';
import IconPhone from '../../assets/ic-phone.png';
import CheckoutPageHeader from '../components/checkout/CheckoutPageHeader';
import PageContainer from '../layouts/PageContainer';
import { v2Colors } from '../design/theme';
import { submitCustomerEnquiry } from '../services/enquiry';
import { useV2Common } from '../state/CommonProvider';
import { SOCIAL_LINKS } from '../config/footerLinks';

const CONTACT_SOCIAL = [
  { Icon: FacebookIcon, ...SOCIAL_LINKS[0] },
  { Icon: InstagramIcon, ...SOCIAL_LINKS[1] },
  { Icon: LinkedInIcon, ...SOCIAL_LINKS[3] },
];

export default function ContactUs() {
  const { isDesktop } = useV2Common();
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const openUrl = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const onSubmit = async (data) => {
    setSubmitting(true);
    setBanner(null);
    try {
      await submitCustomerEnquiry(data);
      setBanner({ severity: 'success', message: 'Enquiry sent successfully!' });
      reset();
    } catch {
      setBanner({ severity: 'error', message: 'Failed to send query. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer sx={{ px: { xs: 1.5, sm: 2 }, pb: 4, maxWidth: { xs: 720, lg: 1100 } }}>
      <CheckoutPageHeader backTo="/v2" title="Contact Us" />

      <Box
        sx={{
          display: 'flex',
          flexDirection: isDesktop ? 'row' : 'column',
          gap: 2,
          alignItems: 'flex-start',
        }}
      >
        <Card
          elevation={0}
          sx={{
            flex: isDesktop ? '0 0 320px' : '1 1 auto',
            width: '100%',
            p: 2.5,
            borderRadius: 2,
            border: `1px solid ${v2Colors.primary}`,
            bgcolor: v2Colors.surface,
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Contact Us
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, opacity: 0.85 }}>
            <Box component="img" src={IconAddress} alt="" sx={{ width: 20, height: 20, mt: 0.25 }} />
            <Typography variant="body2">
              Country Chicken Co. PVT LTD, 5th floor, Sreenidhi Grand Building, Kavuri Hills Phase-1,
              Madhapur, Hyderabad, Telangana 500081
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, opacity: 0.85 }}>
            <Box component="img" src={IconEmail} alt="" sx={{ width: 20, height: 20 }} />
            <Typography variant="body2">customer.care@countrychickenco.in</Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, mb: 3, opacity: 0.85 }}>
            <Box component="img" src={IconPhone} alt="" sx={{ width: 20, height: 20 }} />
            <Typography variant="body2">+91 8096110961</Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {CONTACT_SOCIAL.map(({ Icon, label, url }) => (
              <Icon
                key={label}
                onClick={() => openUrl(url)}
                sx={{
                  color: v2Colors.primary,
                  fontSize: 36,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.85 },
                }}
                aria-label={label}
              />
            ))}
          </Box>
        </Card>

        <Card
          elevation={0}
          sx={{
            flex: 1,
            width: '100%',
            p: { xs: 2.5, md: 4 },
            borderRadius: 2,
            bgcolor: v2Colors.primary,
            color: v2Colors.canvas,
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: 'inherit' }}>
            Send a message
          </Typography>

          {banner ? (
            <Alert severity={banner.severity} sx={{ mb: 2 }} onClose={() => setBanner(null)}>
              {banner.message}
            </Alert>
          ) : null}

          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Box
              sx={{
                display: 'flex',
                flexDirection: isDesktop ? 'row' : 'column',
                gap: 2,
                mb: 2,
              }}
            >
              <TextField
                fullWidth
                label="First Name"
                placeholder="Enter your first name"
                variant="standard"
                {...register('firstName', { required: 'Required field' })}
                error={Boolean(errors.firstName)}
                helperText={errors.firstName?.message}
                InputLabelProps={{ sx: { color: v2Colors.canvas } }}
                InputProps={{ sx: { color: v2Colors.canvas, '&:before': { borderColor: v2Colors.canvas } } }}
              />
              <TextField
                fullWidth
                label="Last Name"
                placeholder="Enter your last name"
                variant="standard"
                {...register('lastName', { required: 'Required field' })}
                error={Boolean(errors.lastName)}
                helperText={errors.lastName?.message}
                InputLabelProps={{ sx: { color: v2Colors.canvas } }}
                InputProps={{ sx: { color: v2Colors.canvas, '&:before': { borderColor: v2Colors.canvas } } }}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: isDesktop ? 'row' : 'column',
                gap: 2,
                mb: 2,
              }}
            >
              <TextField
                fullWidth
                label="Email Address"
                placeholder="Enter your email"
                variant="standard"
                {...register('emailId', {
                  required: 'Required field',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                error={Boolean(errors.emailId)}
                helperText={errors.emailId?.message}
                InputLabelProps={{ sx: { color: v2Colors.canvas } }}
                InputProps={{ sx: { color: v2Colors.canvas, '&:before': { borderColor: v2Colors.canvas } } }}
              />
              <TextField
                fullWidth
                label="Mobile Number"
                placeholder="Enter your mobile number"
                variant="standard"
                {...register('mobileNo', {
                  required: 'Required field',
                  pattern: {
                    value: /^[7896]\d{9}$/,
                    message: 'Invalid mobile number',
                  },
                })}
                error={Boolean(errors.mobileNo)}
                helperText={errors.mobileNo?.message}
                InputLabelProps={{ sx: { color: v2Colors.canvas } }}
                InputProps={{ sx: { color: v2Colors.canvas, '&:before': { borderColor: v2Colors.canvas } } }}
              />
            </Box>

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Write your message here"
              placeholder="Enter your message"
              variant="standard"
              sx={{ mb: 2 }}
              {...register('userMsg', { required: 'Required field' })}
              error={Boolean(errors.userMsg)}
              helperText={errors.userMsg?.message}
              InputLabelProps={{ sx: { color: v2Colors.canvas } }}
              InputProps={{ sx: { color: v2Colors.canvas, '&:before': { borderColor: v2Colors.canvas } } }}
            />

            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              sx={{
                bgcolor: v2Colors.canvas,
                color: v2Colors.primary,
                fontWeight: 700,
                borderRadius: 999,
                px: 4,
                mb: 2,
                '&:hover': { bgcolor: v2Colors.white },
              }}
            >
              {submitting ? 'Sending…' : 'Send'}
            </Button>
          </Box>

          <Typography variant="body2" sx={{ color: v2Colors.canvas, opacity: 0.9 }}>
            Please visit our{' '}
            <MuiLink component={Link} to="/v2/privacy" sx={{ color: 'inherit', fontWeight: 600 }}>
              privacy policy
            </MuiLink>{' '}
            page to know more about our policies.
          </Typography>
          <Typography variant="body2" sx={{ color: v2Colors.canvas, opacity: 0.9, mt: 1 }}>
            To know about our terms and conditions, please visit our{' '}
            <MuiLink component={Link} to="/v2/terms" sx={{ color: 'inherit', fontWeight: 600 }}>
              terms and conditions
            </MuiLink>{' '}
            page.
          </Typography>
        </Card>
      </Box>
    </PageContainer>
  );
}
